import {useRef, useState} from 'react';
import type {FieldValues, UseFormReturn} from 'react-hook-form';

import {rebaseForm} from '../utils/rebaseForm';

type VersionedRecord = {id: string; version: number};

/**
 * The version an editor of `record` sends. It moves only with the editor's own writes
 * (`adoptVersion`) and `reread`, so a refetch never adopts another author's change. `reread` makes
 * the record read again the form's baseline before it adopts its version, so a retry after a
 * refused write keeps both the other author's changes and the author's own; a read that finishes
 * once the editor shows another record is dropped. The editor hands its form over in `formRef`.
 */
export function useHeldVersion<
  TRecord extends VersionedRecord,
  TValues extends FieldValues,
>(props: {
  record: TRecord | undefined;
  read: () => Promise<TRecord>;
  toValues: (record: TRecord) => TValues;
  onRead?: (record: TRecord) => void;
}) {
  const [held, setHeld] = useState<VersionedRecord | null>(null);
  if (props.record && held?.id !== props.record.id) {
    setHeld({id: props.record.id, version: props.record.version});
  }
  const formRef = useRef<UseFormReturn<TValues> | null>(null);
  const recordIdRef = useRef(props.record?.id);
  recordIdRef.current = props.record?.id;
  const reread = () =>
    props.read().then((current) => {
      if (current.id !== recordIdRef.current) {
        return;
      }
      if (formRef.current) {
        rebaseForm(formRef.current, props.toValues(current));
      }
      setHeld({id: current.id, version: current.version});
      props.onRead?.(current);
    });
  return {
    version: held && held.id === props.record?.id ? held.version : props.record?.version,
    /** A write that finishes once the editor shows another record leaves that record's version. */
    adoptVersion: (version: number, recordId: string) =>
      setHeld((current) => (current?.id === recordId ? {...current, version} : current)),
    formRef,
    reread,
    /** After a refused write, which is already reported, so a failed read is not. */
    reloadStale: () => reread().catch(() => undefined),
  };
}
